import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { currentCaptain, parseCaptainQueue } from '@4cm/core/rotatingCaptain';
import { generateMatchNarrative, generatePreMatchBrief } from '@4cm/core/matchReport';
import { useAuth } from '../contexts/AuthContext';
import { BrainOrb, Button, Card, GlassPill } from '../components/primitives';
import { TacticsBoard2D } from '../components/tactics2d';
import { TacticsBoard3D } from '../components/tactics3d';
import { TacticsBrainPanel } from '../components/TacticsBrainPanel';
import { useCalibration } from '../contexts/CalibrationContext';
import { useGame } from '../lib/queries/useTeamGames';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useTeamPlayers } from '../lib/queries/useTeamPlayers';
import { supabase, isPlaceholderSupabase } from '../lib/supabase';
import './page-shell.css';
import './Games.css';

type TagKind = 'ATTACK' | 'DEFEND' | 'PRESS' | 'RECOVER';

interface LiveTag {
  id: string;
  kind: TagKind;
  /** Seconds since the timer started. */
  t: number;
  note?: string;
}

const TAG_BUTTONS: { kind: TagKind; label: string; cls: string }[] = [
  { kind: 'ATTACK', label: 'Attack', cls: 'gameday-tag-btn--attack' },
  { kind: 'DEFEND', label: 'Defend', cls: 'gameday-tag-btn--defend' },
  { kind: 'PRESS', label: 'Press', cls: 'gameday-tag-btn--press' },
  { kind: 'RECOVER', label: 'Recover', cls: 'gameday-tag-btn--recover' },
];

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function isLineupArray(v: unknown): v is { player_id: string }[] {
  return Array.isArray(v) && v.every((e) => typeof e === 'object' && e !== null && 'player_id' in e);
}

export function GameDay() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { team } = useActiveTeam();
  const { row, state, errorMessage, refresh } = useGame(id);
  const { rows: players } = useTeamPlayers(team?.id, false, { limit: 30 });
  const { level } = useCalibration();
  const [boardMode, setBoardMode] = useState<'2d' | '3d'>('2d');

  const [lineup, setLineup] = useState<Set<string>>(new Set());
  const [savingLineup, setSavingLineup] = useState(false);
  const [lineupSaved, setLineupSaved] = useState(false);

  const [timerStart, setTimerStart] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const [tags, setTags] = useState<LiveTag[]>([]);

  const [reportNotes, setReportNotes] = useState('');
  const [savingReport, setSavingReport] = useState(false);
  const [reportSaved, setReportSaved] = useState(false);
  const [generatingNarrative, setGeneratingNarrative] = useState(false);
  const [narrativeError, setNarrativeError] = useState<string | null>(null);

  // Pre-match brief (games.pregame_brief) — the plain-English explanation
  // beside the tactics board.
  const [brief, setBrief] = useState('');
  const [savingBrief, setSavingBrief] = useState(false);
  const [briefSaved, setBriefSaved] = useState(false);
  const [generatingBrief, setGeneratingBrief] = useState(false);
  const [briefError, setBriefError] = useState<string | null>(null);

  // Rotating-captain register (Ben item #7) — read the team's queue and
  // surface the player whose turn it is for this game. Read-only here;
  // queue management UI lives on the Players page (follow-up).
  const [captainQueueRaw, setCaptainQueueRaw] = useState<unknown>(null);
  useEffect(() => {
    if (!team?.id) return;
    if (isPlaceholderSupabase()) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('teams')
        .select('rotating_captain_queue')
        .eq('id', team.id)
        .maybeSingle();
      if (cancelled) return;
      setCaptainQueueRaw(data?.rotating_captain_queue ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [team?.id]);

  const captainPlayerId = useMemo(() => currentCaptain(parseCaptainQueue(captainQueueRaw)), [captainQueueRaw]);
  const captainName = useMemo(
    () => (captainPlayerId ? players.find((p) => p.id === captainPlayerId)?.name ?? null : null),
    [captainPlayerId, players],
  );

  // Hydrate lineup + report from the game row when it loads.
  useEffect(() => {
    if (!row) return;
    if (isLineupArray(row.lineup)) {
      setLineup(new Set(row.lineup.map((e) => e.player_id)));
    }
    setReportNotes(row.match_report ?? '');
    setBrief(row.pregame_brief ?? '');
  }, [row]);

  // 1Hz tick when the timer is running.
  useEffect(() => {
    if (timerStart == null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [timerStart]);

  const elapsed = useMemo(() => {
    if (timerStart == null) return 0;
    return Math.max(0, Math.round((now - timerStart) / 1000));
  }, [timerStart, now]);

  const togglePlayer = (playerId: string) =>
    setLineup((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) next.delete(playerId);
      else next.add(playerId);
      return next;
    });

  const handleSaveLineup = async () => {
    if (!row) return;
    if (isPlaceholderSupabase()) return;
    setSavingLineup(true);
    setLineupSaved(false);
    const { error } = await supabase
      .from('games')
      .update({ lineup: Array.from(lineup).map((player_id) => ({ player_id })) })
      .eq('id', row.id);
    setSavingLineup(false);
    if (!error) {
      setLineupSaved(true);
      window.setTimeout(() => setLineupSaved(false), 2000);
      refresh();
    }
  };

  const handleAddTag = (kind: TagKind) => {
    if (timerStart == null) {
      setTimerStart(Date.now());
      setNow(Date.now());
    }
    setTags((prev) => [
      { id: `tag-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, kind, t: timerStart == null ? 0 : elapsed },
      ...prev,
    ]);
  };

  const handleSaveReport = async () => {
    if (!row) return;
    if (isPlaceholderSupabase()) return;
    setSavingReport(true);
    setReportSaved(false);
    const { error } = await supabase
      .from('games')
      .update({ match_report: reportNotes.trim() || null })
      .eq('id', row.id);
    setSavingReport(false);
    if (!error) {
      setReportSaved(true);
      window.setTimeout(() => setReportSaved(false), 2000);
    }
  };

  const handleGenerateNarrative = useCallback(async () => {
    if (!row || !user) return;
    if (isPlaceholderSupabase()) {
      setNarrativeError('Connect Supabase to generate narratives.');
      return;
    }
    setGeneratingNarrative(true);
    setNarrativeError(null);
    const result = await generateMatchNarrative({ gameId: row.id, coachId: user.id });
    setGeneratingNarrative(false);
    if (!result.ok || !result.text) {
      setNarrativeError(result.error ?? 'Could not generate narrative.');
      return;
    }
    // Append to existing notes (or replace if empty) — coach can edit.
    setReportNotes((prev) => (prev.trim() ? `${prev.trim()}\n\n${result.text}` : result.text!));
  }, [row, user]);

  const handleGenerateBrief = useCallback(async () => {
    if (!row || !user) return;
    if (isPlaceholderSupabase()) { setBriefError('Connect Supabase to generate a brief.'); return; }
    setGeneratingBrief(true);
    setBriefError(null);
    const result = await generatePreMatchBrief({ gameId: row.id, coachId: user.id });
    setGeneratingBrief(false);
    if (!result.ok || !result.text) { setBriefError(result.error ?? 'Could not generate a brief.'); return; }
    setBrief(result.text);
  }, [row, user]);

  const handleSaveBrief = async () => {
    if (!row) return;
    if (isPlaceholderSupabase()) return;
    setSavingBrief(true);
    setBriefSaved(false);
    const { error } = await supabase
      .from('games')
      .update({ pregame_brief: brief.trim() || null })
      .eq('id', row.id);
    setSavingBrief(false);
    if (!error) {
      setBriefSaved(true);
      window.setTimeout(() => setBriefSaved(false), 2000);
    }
  };

  if (state === 'loading') return <main className="main page"><p className="page-empty">Loading…</p></main>;
  if (state === 'error') {
    return (
      <main className="main page">
        <Card variant="opaque">
          <p className="page-empty">Couldn&apos;t load game.{errorMessage ? ` (${errorMessage})` : null}</p>
        </Card>
      </main>
    );
  }
  if (!row) {
    return (
      <main className="main page">
        <Card variant="opaque">
          <p className="page-empty">Game not found. <Link to="/games">Back to games</Link></p>
        </Card>
      </main>
    );
  }

  const gameKey = `tactics-3d-game-${row.id}`;
  const use3d = level === 'academy' && boardMode === '3d';

  return (
    <main className="main page gameday-page">
      <header className="page-head">
        <BrainOrb size="lg" ariaLabel="Brain" />
        <div>
          <span className="page-eyebrow">
            <Link to="/games">← Games</Link> · {row.date}
          </span>
          <h1 className="page-title">vs {row.opponent}</h1>
          <div className="games-next__pills">
            <GlassPill mono>{row.home_away}</GlassPill>
            {row.competition && <GlassPill mono>{row.competition}</GlassPill>}
            {row.venue && <GlassPill mono>{row.venue}</GlassPill>}
            {captainName && (
              <GlassPill mono>
                <span aria-hidden="true">🧢</span> Captain · {captainName}
              </GlassPill>
            )}
          </div>
        </div>
      </header>

      <section className="gameday-section">
        <div className="gameday-section__head">
          <span className="page-eyebrow">Lineup · {lineup.size} selected</span>
          <Button variant="primary" size="touchline" onClick={() => void handleSaveLineup()} disabled={savingLineup}>
            {savingLineup ? 'Saving…' : 'Save lineup'}
          </Button>
          {lineupSaved && <span className="games-form__error" style={{ background: 'rgba(46, 204, 113, 0.12)', borderColor: 'rgba(46, 204, 113, 0.4)', color: 'rgb(180, 240, 200)' }}>Saved</span>}
        </div>
        {players.length === 0 ? (
          <Card variant="opaque">
            <p className="page-empty">No players on the roster yet.</p>
          </Card>
        ) : (
          <div className="games-form__corners" style={{ gap: 6, flexWrap: 'wrap', display: 'flex' }}>
            {players.map((p) => (
              <button
                key={p.id}
                type="button"
                className="principles-form__corner-chip"
                data-active={lineup.has(p.id) || undefined}
                onClick={() => togglePlayer(p.id)}
              >
                {p.name}{p.position ? ` · ${p.position}` : ''}
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="gameday-section">
        <div className="gameday-section__head">
          <span className="page-eyebrow">Pre-match plan · tactics + brief</span>
          <Button
            variant="glass"
            size="touchline"
            onClick={() => void handleGenerateBrief()}
            disabled={generatingBrief || !user}
            title="Brain drafts a brief from your principles, the opponent, and where you are in the season."
          >
            {generatingBrief ? 'Drafting…' : '✨ Recommend brief'}
          </Button>
          <Button variant="primary" size="touchline" onClick={() => void handleSaveBrief()} disabled={savingBrief}>
            {savingBrief ? 'Saving…' : 'Save brief'}
          </Button>
          {briefSaved && <span className="gameday-saved">Saved</span>}
        </div>
        {briefError && <p className="games-form__error">{briefError}</p>}
        <textarea
          className="gameday-textarea"
          placeholder="The shape, the key tactical points vs this opponent, what to watch for. Tap “Recommend brief” to draft from your principles + season focus, then ask Brain on the board to sketch a shape — and tweak it."
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          rows={4}
        />
        {level === 'academy' && (
          <div className="gameday-board-toggle" role="group" aria-label="Board view">
            <Button variant={boardMode === '2d' ? 'primary' : 'glass'} size="touchline" onClick={() => setBoardMode('2d')}>2D</Button>
            <Button variant={boardMode === '3d' ? 'primary' : 'glass'} size="touchline" onClick={() => setBoardMode('3d')}>3D</Button>
          </div>
        )}
        <div className="gameday-board-split">
          <div className="gameday-board-split__board">
            {use3d ? (
              <TacticsBoard3D homeFormation="4-3-3" persistKey={gameKey} />
            ) : (
              <TacticsBoard2D
                homeFormation="4-3-3"
                showAway
                persistKey={gameKey}
                homeLabel={team?.name ?? 'Us'}
                awayLabel={row.opponent}
              />
            )}
          </div>
          <aside className="gameday-board-split__brain">
            <TacticsBrainPanel
              persistKey={gameKey}
              surface={use3d ? '3d' : '2d'}
              teamId={team?.id ?? null}
            />
          </aside>
        </div>
      </section>

      <section className="gameday-section">
        <div className="gameday-section__head">
          <span className="page-eyebrow">
            Live tagging · {timerStart == null ? '—' : formatTime(elapsed)}
            {timerStart != null && ' (running)'}
          </span>
          {timerStart != null && (
            <Button
              variant="glass"
              size="touchline"
              onClick={() => {
                setTimerStart(null);
              }}
            >
              Stop timer
            </Button>
          )}
        </div>
        <div className="gameday-tag-bar" role="group" aria-label="Transition tag buttons">
          {TAG_BUTTONS.map((b) => (
            <button
              key={b.kind}
              type="button"
              className={`gameday-tag-btn ${b.cls}`}
              onClick={() => handleAddTag(b.kind)}
            >
              {b.label}
            </button>
          ))}
        </div>
        {tags.length > 0 && (
          <div className="gameday-tag-log" aria-live="polite">
            {tags.map((t) => (
              <div key={t.id} className="gameday-tag-row">
                <span className="gameday-tag-row__time">{formatTime(t.t)}</span>
                <span className="gameday-tag-row__kind">{t.kind}</span>
              </div>
            ))}
          </div>
        )}
        {tags.length === 0 && timerStart == null && (
          <p className="page-empty" style={{ fontStyle: 'italic' }}>
            Tap a tag to start the timer and log the first transition.
          </p>
        )}
      </section>

      <section className="gameday-section gameday-print-target">
        <div className="gameday-section__head">
          <span className="page-eyebrow">Match report</span>
          <Button
            variant="glass"
            size="touchline"
            onClick={() => void handleGenerateNarrative()}
            disabled={generatingNarrative || !user}
            title="Brain drafts a 3-4 sentence narrative from the team's recent ratings and the game shape."
          >
            {generatingNarrative ? 'Drafting…' : '✨ Draft with Brain'}
          </Button>
          <Button variant="primary" size="touchline" onClick={() => void handleSaveReport()} disabled={savingReport}>
            {savingReport ? 'Saving…' : 'Save report'}
          </Button>
          <Button
            variant="glass"
            size="touchline"
            onClick={() => window.print()}
            title="Use the browser print dialog to save / share as PDF"
          >
            Print / PDF
          </Button>
          {reportSaved && (
            <span className="games-form__error" style={{ background: 'rgba(46, 204, 113, 0.12)', borderColor: 'rgba(46, 204, 113, 0.4)', color: 'rgb(180, 240, 200)' }}>
              Saved
            </span>
          )}
        </div>
        {narrativeError && (
          <p className="games-form__error">{narrativeError}</p>
        )}
        <textarea
          className="gameday-textarea"
          placeholder="Two strengths, one development focus, one specific moment to revisit in next session. Or tap “Draft with Brain” to start from the AI summary and edit."
          value={reportNotes}
          onChange={(e) => setReportNotes(e.target.value)}
          rows={6}
        />
      </section>
    </main>
  );
}
